import { WAVEFORM_BARS_COUNT } from '../model/constants'

import { loadVoiceFileBlob } from './get-playable-voice-src'

type VoicePeaksResult = {
  peaks: number[]
  durationMs: number
}

const peaksCache = new Map<string, VoicePeaksResult>()

const extractPeaksFromAudioBuffer = (buffer: AudioBuffer, barCount: number): number[] => {
  const channel = buffer.getChannelData(0)
  if (!channel.length) return Array.from({ length: barCount }, () => 0.08)

  const blockSize = Math.max(1, Math.floor(channel.length / barCount))
  const peaks: number[] = []

  for (let barIndex = 0; barIndex < barCount; barIndex += 1) {
    const start = barIndex * blockSize
    const end = Math.min(channel.length, start + blockSize)
    let peak = 0

    for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
      peak = Math.max(peak, Math.abs(channel[sampleIndex] ?? 0))
    }

    peaks.push(Math.min(1, peak))
  }

  return peaks
}

export const extractPeaksFromBlob = async (
  blob: Blob,
  barCount = WAVEFORM_BARS_COUNT,
): Promise<VoicePeaksResult> => {
  const arrayBuffer = await blob.arrayBuffer()
  const audioContext = new AudioContext()

  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
    return {
      peaks: extractPeaksFromAudioBuffer(audioBuffer, barCount),
      durationMs: audioBuffer.duration * 1000,
    }
  } finally {
    void audioContext.close()
  }
}

export const getCachedVoiceDurationSec = (fileCookie: string): number => {
  const cached = peaksCache.get(fileCookie)
  if (!cached?.durationMs) return 0
  return cached.durationMs / 1000
}

export const getVoiceMessagePeaks = async (
  fileCookie: string,
  barCount = WAVEFORM_BARS_COUNT,
): Promise<VoicePeaksResult> => {
  const cached = peaksCache.get(fileCookie)
  if (cached) return cached

  const arrayBuffer = await (await loadVoiceFileBlob(fileCookie)).arrayBuffer()
  const audioContext = new AudioContext()

  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const result = {
      peaks: extractPeaksFromAudioBuffer(audioBuffer, barCount),
      durationMs: audioBuffer.duration * 1000,
    }
    peaksCache.set(fileCookie, result)
    return result
  } finally {
    void audioContext.close()
  }
}
